import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Store,
  TrendingUp,
  Users,
  Handshake,
  CheckCircle,
  Send,
  Utensils,
  Target
} from 'lucide-react';
import Navigation from '../components/Navigation';

const BecomeVendorPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    business: '',
    email: '',
    phone: '',
    boothType: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Vendor application:', formData);
    // Handle form submission
  };

  const boothTypes = [
    {
      type: 'Standard Booth',
      size: '10x10 ft',
      price: '$1,200 - $1,800',
      icon: Store,
      includes: [
        'Premium floor space',
        'Table & chairs included',
        'Power outlet access',
        'WiFi connectivity',
        '2 exhibitor passes'
      ]
    },
    {
      type: 'Premium Booth',
      size: '20x20 ft',
      price: '$3,500 - $5,000',
      icon: TrendingUp,
      includes: [
        'Corner or featured location',
        'Full booth setup',
        'Multiple power outlets',
        'Enhanced visibility',
        '4 exhibitor passes',
        'Social media feature'
      ]
    },
    {
      type: 'Food & Beverage Zone',
      size: 'Custom',
      price: 'Starting at $2,500',
      icon: Utensils,
      includes: [
        'High-traffic food court area',
        'Kitchen access & utilities',
        'Custom setup support',
        'Health permit assistance',
        '3 staff passes'
      ]
    }
  ];

  const benefits = [
    {
      icon: Target,
      title: 'Exposure',
      description: '10,000+ attendees over 3 days'
    },
    {
      icon: Users,
      title: 'Foot Traffic',
      description: 'Prime marketplace placement'
    },
    {
      icon: Handshake,
      title: 'Support',
      description: 'Dedicated vendor success team'
    }
  ];

  const applicationSteps = [
    {
      number: '1',
      title: 'Complete Application',
      description: 'Fill out our vendor application form with business details'
    },
    {
      number: '2',
      title: 'Get Approved',
      description: 'Our team reviews and responds within 5-7 business days'
    },
    {
      number: '3',
      title: 'Prepare for the Event',
      description: 'Receive your vendor packet and setup instructions'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <div className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1920&h=1080&fit=crop')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B]/95 via-[#0B1B3B]/85 to-[#0B1B3B]/75" />
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Become a <span className="text-[#CFA64B]">Vendor</span>
          </h1>
          <p className="text-xl md:text-2xl mb-10 text-gray-200">
            Bring your brand to life at the KNOWN CON Marketplace.
          </p>
          <Button className="bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold px-10 py-6 text-lg">
            Apply to Exhibit
          </Button>
        </div>
      </div>

      {/* Section 1: Vendor Opportunities */}
      <div className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Vendor Opportunities</h2>
            <p className="text-xl text-gray-600">Choose the booth that fits your vision</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {boothTypes.map((booth, idx) => {
              const Icon = booth.icon;
              return (
                <Card 
                  key={idx}
                  className="border-2 border-gray-200 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300"
                >
                  <CardContent className="p-8">
                    <div className="w-16 h-16 mb-6 bg-[#CFA64B]/10 rounded-full flex items-center justify-center">
                      <Icon className="w-8 h-8 text-[#CFA64B]" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0B1B3B] mb-2">{booth.type}</h3>
                    <div className="text-sm text-gray-600 mb-2">{booth.size}</div>
                    <div className="text-2xl font-bold text-[#CFA64B] mb-6">{booth.price}</div>
                    <div className="border-t border-gray-200 pt-6">
                      <p className="text-sm font-bold text-[#0B1B3B] mb-3">Includes:</p>
                      <ul className="space-y-2">
                        {booth.includes.map((item, iidx) => (
                          <li key={iidx} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-[#CFA64B] flex-shrink-0 mt-0.5" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Section 2: What You'll Get */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">What You'll Get</h2>
            <p className="text-xl text-gray-600">Benefits of being a KNOWN CON vendor</p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-24 h-24 mx-auto mb-6 bg-[#CFA64B]/10 rounded-full flex items-center justify-center">
                <TrendingUp className="w-12 h-12 text-[#CFA64B]" />
              </div>
              <h3 className="text-2xl font-bold text-[#0B1B3B] mb-3">Exposure</h3>
              <p className="text-gray-600">Connect with 10,000+ purpose-driven attendees</p>
            </div>
            <div className="text-center">
              <div className="w-24 h-24 mx-auto mb-6 bg-[#CFA64B]/10 rounded-full flex items-center justify-center">
                <Users className="w-12 h-12 text-[#CFA64B]" />
              </div>
              <h3 className="text-2xl font-bold text-[#0B1B3B] mb-3">Foot Traffic</h3>
              <p className="text-gray-600">Prime marketplace location with high visibility</p>
            </div>
            <div className="text-center">
              <div className="w-24 h-24 mx-auto mb-6 bg-[#CFA64B]/10 rounded-full flex items-center justify-center">
                <Handshake className="w-12 h-12 text-[#CFA64B]" />
              </div>
              <h3 className="text-2xl font-bold text-[#0B1B3B] mb-3">Support</h3>
              <p className="text-gray-600">Dedicated team from setup to teardown</p>
            </div>
          </div>
        </div>
      </div>

      {/* Section 3: How to Apply */}
      <div className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">How to Apply</h2>
            <p className="text-xl text-gray-600">Three simple steps to become a vendor</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {applicationSteps.map((step, idx) => (
              <Card key={idx} className="border-2 border-gray-200 relative">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-6 bg-[#CFA64B] text-white rounded-full flex items-center justify-center text-3xl font-bold">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold text-[#0B1B3B] mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Button className="bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold px-10 py-6 text-lg">
              Apply Now
            </Button>
          </div>
        </div>
      </div>

      {/* Section 4: Vendor Support */}
      <div className="py-20" style={{ backgroundColor: '#FFF9E9' }}>
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <div className="mb-8">
            <Handshake className="w-16 h-16 text-[#CFA64B] mx-auto mb-6" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-6">Vendor Support</h2>
          <p className="text-xl text-gray-700 mb-10 leading-relaxed">
            Our vendor team will guide you from setup to showcase. We're committed to making your 
            experience seamless so you can focus on connecting with attendees and growing your business.
          </p>
          <Button className="bg-[#0B1B3B] hover:bg-[#1a2f5a] text-white font-bold px-10 py-6 text-lg">
            <Send className="w-5 h-5 mr-2" />
            Contact Vendor Team
          </Button>
        </div>
      </div>

      {/* Section 5: Closing CTA */}
      <div className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B] via-[#1a2f5a] to-[#CFA64B]" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            Ready to showcase your brand?
          </h2>
          <p className="text-xl mb-10 text-gray-200">
            Join the marketplace where faith, culture, and purpose meet
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-white text-[#0B1B3B] hover:bg-gray-100 font-bold px-10 py-6 text-lg">
              Apply Now
            </Button>
            <Button variant="outline" className="bg-transparent border-white text-white hover:bg-white/10 border-2 px-10 py-6 text-lg">
              View Sponsorships
            </Button>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="py-6 bg-gray-50 border-t border-gray-200">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <a href="/attendee-guide" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">
              Attendee Guide
            </a>
            <span className="text-gray-400">|</span>
            <a href="/faqs" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">
              FAQs
            </a>
            <span className="text-gray-400">|</span>
            <a href="#contact" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">
              Contact Support
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0f172a] text-white py-12">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
          <p className="text-gray-400 text-sm">© 2025 KNOWN CON. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BecomeVendorPage;
